import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  BarChart3, CalendarClock, Clock, HelpCircle, Layers, RefreshCw, Search,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import {
  AppBadge, AppButton, AppCard, AppCardContent, Callout, EmptyState, Input, NativeSelect, PageHeader, StatusBadge,
} from "../ui";
import Shimmer from "../Common/Shimmer";
import {
  ACTION_LABELS, MODE_LABELS, STATUS, STATUS_OPTIONS, formatSchedule, plural,
} from "./studentAssessmentRules";

const actionVariant = (status) => {
  if (status === "available") return "default";
  if (status === "in_progress") return "outline-primary";
  return "outline";
};

/** One fact on a card — an icon and a few words. */
function Fact({ icon: Icon, children }) {
  return (
    <span className="tw-inline-flex tw-items-center tw-gap-1.5">
      <Icon size={14} aria-hidden="true" className="tw-shrink-0 tw-text-content-subtle" />
      {children}
    </span>
  );
}

/**
 * StudentAssessmentsPage — `/s/assessments`, the student's quizzes and tests.
 *
 * The server says where each one stands (`studentStatus`) — available, in
 * progress, completed, expired, upcoming — with the schedule a rule-based
 * assignment gives this student; the page only shows it.
 */
const StudentAssessmentsPage = () => {
  const navigate = useNavigate();
  const [assessments, setAssessments] = useState([]);
  const [state, setState] = useState("loading");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const fetchAssessments = useCallback(async () => {
    setState("loading");
    try {
      const { data } = await apiClient.get("/student-assessments");
      setAssessments(data.assessments || []);
      setState("ready");
    } catch {
      setState("error");
    }
  }, []);

  useEffect(() => { fetchAssessments(); }, [fetchAssessments]);

  const filtered = useMemo(() => assessments.filter((a) => {
    if (search && !a.title.toLowerCase().includes(search.toLowerCase())) return false;
    if (statusFilter && a.studentStatus !== statusFilter) return false;
    return true;
  }), [assessments, search, statusFilter]);

  const handleAction = (a) => {
    // Standalone assessment — drop any remembered test-series context.
    sessionStorage.removeItem("vc-active-series");
    if (a.studentStatus === "in_progress" && a.inProgressAttemptId) {
      navigate(`/s/assessment/take/${a.inProgressAttemptId}`);
    } else {
      navigate(`/s/assessment/${a._id}`);
    }
  };

  const filtersOn = Boolean(search || statusFilter);

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      <PageHeader title="My Assessments" description="Your quizzes and tests" icon={HelpCircle} />

      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3">
        <div className="tw-min-w-[200px] tw-flex-1">
          <Input
            type="search"
            aria-label="Search assessments"
            placeholder="Search assessments…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            leading={<Search size={16} />}
          />
        </div>
        <div className="tw-w-44">
          <NativeSelect aria-label="Status" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All statuses</option>
            {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </NativeSelect>
        </div>
      </div>

      {state === "loading" && (
        <div aria-busy="true" aria-label="Loading assessments">
          <Shimmer height={150} count={3} />
        </div>
      )}

      {state === "error" && (
        <Callout
          tone="danger"
          role="alert"
          action={<AppButton size="xs" variant="outline" onClick={fetchAssessments}>Try again</AppButton>}
        >
          Your assessments could not be loaded.
        </Callout>
      )}

      {state === "ready" && filtered.length === 0 && (
        <EmptyState
          icon={<HelpCircle size={40} />}
          title="No assessments"
          description={filtersOn ? "No assessments match your filters." : "No assessments have been given to you yet."}
          action={filtersOn ? (
            <AppButton size="sm" variant="outline" onClick={() => { setSearch(""); setStatusFilter(""); }}>
              Clear filters
            </AppButton>
          ) : null}
        />
      )}

      {state === "ready" && filtered.length > 0 && (
        <ul className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-3 tw-p-0">
          {filtered.map((a) => {
            const status = STATUS[a.studentStatus] || STATUS.available;
            const sections = a.isSectioned ? (a.sections || []).length : 0;
            return (
              <li key={a._id}>
                <AppCard as="article" aria-label={a.title}>
                  <AppCardContent className="tw-flex tw-flex-col tw-gap-3">
                    <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
                      <StatusBadge status={status.tone}>{status.label}</StatusBadge>
                      {MODE_LABELS[a.mode] && <AppBadge variant="secondary" size="sm">{MODE_LABELS[a.mode]}</AppBadge>}
                    </div>
                    <div className="tw-flex tw-flex-col tw-gap-1">
                      <h2 className="tw-m-0 tw-text-base tw-font-semibold tw-text-content">{a.title}</h2>
                      {a.description && (
                        <p className="tw-m-0 tw-line-clamp-2 tw-text-[0.8125rem] tw-text-content-muted">{a.description}</p>
                      )}
                    </div>
                    <div className="tw-flex tw-flex-wrap tw-gap-x-5 tw-gap-y-1.5 tw-text-[0.8125rem] tw-text-content-muted">
                      <Fact icon={HelpCircle}>{plural(a.questionCount || 0, "question")}</Fact>
                      {sections > 1 && <Fact icon={Layers}>{plural(sections, "section")}</Fact>}
                      <Fact icon={Clock}>{a.timeLimit > 0 ? `${a.timeLimit} min` : "No time limit"}</Fact>
                      <Fact icon={RefreshCw}>{a.attemptsUsed}/{a.maxAttempts > 0 ? a.maxAttempts : "∞"} attempts</Fact>
                      {a.bestScore > 0 && <Fact icon={BarChart3}>Best {a.bestScore}%</Fact>}
                    </div>
                    <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3 tw-border-0 tw-border-t tw-border-solid tw-border-line-soft tw-pt-3">
                      <div className="tw-flex tw-flex-wrap tw-gap-x-4 tw-gap-y-1 tw-text-[0.8125rem] tw-text-content-muted">
                        {a.startDate && <Fact icon={CalendarClock}>Starts {formatSchedule(a.startDate)}</Fact>}
                        {a.endDate && <Fact icon={CalendarClock}>Ends {formatSchedule(a.endDate)}</Fact>}
                      </div>
                      <AppButton
                        size="sm"
                        variant={actionVariant(a.studentStatus)}
                        onClick={() => handleAction(a)}
                        // Upcoming stays openable — the details page shows the
                        // schedule and keeps "Start" locked until startDate.
                        disabled={a.studentStatus === "expired"}
                      >
                        {ACTION_LABELS[a.studentStatus] || "View"}
                      </AppButton>
                    </div>
                  </AppCardContent>
                </AppCard>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default StudentAssessmentsPage;
